// The /create studio's "Type words" (text to speech) and "AI song"
// (Treblo generation) forms, in design language F. They report exactly what
// TextToSpeechInputView and GenerateSongView do (same fields, same limits,
// same options), so the request body is unchanged.
import React, { useState } from "react";
import toast from "react-hot-toast";
import ReactCountryFlag from "react-country-flag";
import {
  accents as ACCENTS_JS,
  expressions as EXPRESSIONS_JS,
  genders as GENDERS_JS,
} from "@/components/modals/TextToSpeechInputView";
import {
  GENRE_PRESETS as GENRE_PRESETS_JS,
  MAX_CAPTION,
  MAX_LYRICS,
} from "@/components/modals/GenerateSongView";
import { type MessageKey, useT } from "@jammable/ui-core/src/i18n";

/* eslint-disable @typescript-eslint/no-explicit-any */

const GENDERS = GENDERS_JS as { displayName: string; value: string }[];
const ACCENTS = ACCENTS_JS as { displayName: string; flagCode: string }[];
const EXPRESSIONS = EXPRESSIONS_JS as { displayName: string; emoji: string }[];
const GENRE_PRESETS = GENRE_PRESETS_JS as { label: string; caption: string }[];
const MAX_TTS = 2000;

// Labels only: the option values sent (gender value, accent flagCode, the
// expression's English displayName) stay as they are.
const GENDER_LABEL: Record<string, MessageKey> = {
  female: "studio.tts.gender.female",
  male: "studio.tts.gender.male",
};
const ACCENT_LABEL: Record<string, MessageKey> = {
  US: "studio.tts.accent.US",
  GB: "studio.tts.accent.GB",
  FR: "studio.tts.accent.FR",
  DE: "studio.tts.accent.DE",
  ES: "studio.tts.accent.ES",
  IT: "studio.tts.accent.IT",
  KR: "studio.tts.accent.KR",
  AU: "studio.tts.accent.AU",
  JP: "studio.tts.accent.JP",
};
const EXPRESSION_LABEL: Record<string, MessageKey> = {
  Default: "studio.tts.expr.default",
  Whispered: "studio.tts.expr.whispered",
  Happy: "studio.tts.expr.happy",
  Sad: "studio.tts.expr.sad",
  Angry: "studio.tts.expr.angry",
  Shouting: "studio.tts.expr.shouting",
  Terrified: "studio.tts.expr.terrified",
};
// Genre names read the same in Spanish and Portuguese today; keyed so they
// can differ. The caption a preset fills in is sent as-is (English).
const GENRE_LABEL: Record<string, MessageKey> = {
  Pop: "studio.ai.genre.pop",
  Trap: "studio.ai.genre.trap",
  "R&B": "studio.ai.genre.rnb",
  Indie: "studio.ai.genre.indie",
  "K-pop": "studio.ai.genre.kpop",
  EDM: "studio.ai.genre.edm",
  "Lo-fi": "studio.ai.genre.lofi",
  Country: "studio.ai.genre.country",
};

const Chip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`tw-flex tw-shrink-0 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-border-0 tw-px-3.5 tw-py-2 tw-text-[13px] tw-font-extrabold ${
      active
        ? "tw-bg-jam-ink tw-text-white"
        : "tw-bg-jam-lav tw-text-jam-ink hover:tw-text-jam-purple"
    }`}
  >
    {children}
  </button>
);

const Label = ({ children }: { children: React.ReactNode }) => (
  <div className="tw-text-xs tw-font-extrabold tw-tracking-[0.04em] tw-text-jam-muted">
    {children}
  </div>
);

const TextBox = (props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea
    {...props}
    className="tw-w-full tw-resize-none tw-rounded-[22px] tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-p-4 tw-text-[15px] tw-font-semibold tw-text-jam-ink tw-outline-none placeholder:tw-text-jam-muted focus:tw-border-jam-purple"
  />
);

const UseButton = ({
  disabled,
  onClick,
  children,
}: {
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="tw-h-12 tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-ink tw-text-[15px] tw-font-extrabold tw-text-white disabled:tw-cursor-not-allowed disabled:tw-bg-jam-soft"
  >
    {children}
  </button>
);

export const TypeWords = ({
  selectedVoices,
  onConfirm,
}: {
  selectedVoices: any[];
  onConfirm: (tts: {
    text: string;
    accent: string;
    gender: string;
    expression: string;
  }) => void;
}) => {
  const t = useT();
  const [text, setText] = useState("");
  // Same default as TextToSpeechInputView: the lead voice's TTS gender.
  const [gender, setGender] = useState(
    selectedVoices?.[0]?.tts?.gender === "female" ? "female" : "male",
  );
  const [accent, setAccent] = useState(ACCENTS[0].flagCode);
  const [expression, setExpression] = useState(EXPRESSIONS[0].displayName);

  const confirm = () => {
    if (!text.trim()) {
      toast.error(t("studio.tts.empty"));
      return;
    }
    if (text.length > MAX_TTS) {
      toast.error(t("studio.tts.tooLong", { max: MAX_TTS }));
      return;
    }
    onConfirm({ text: text.trim(), accent, gender, expression });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3.5">
      <TextBox
        aria-label={t("studio.tts.what")}
        placeholder={t("studio.tts.placeholder")}
        rows={4}
        value={text}
        maxLength={MAX_TTS + 50}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="tw-flex tw-flex-col tw-gap-2">
        <Label>{t("studio.tts.baseVoice")}</Label>
        <div className="tw-flex tw-gap-2">
          {GENDERS.map((g) => (
            <Chip
              key={g.value}
              active={gender === g.value}
              onClick={() => setGender(g.value)}
            >
              {GENDER_LABEL[g.value] ? t(GENDER_LABEL[g.value]) : g.displayName}
            </Chip>
          ))}
        </div>
      </div>
      <div className="tw-flex tw-flex-col tw-gap-2">
        <Label>{t("studio.tts.accent")}</Label>
        <div className="tw-flex tw-gap-2 tw-overflow-x-auto tw-pb-1 md:tw-flex-wrap">
          {ACCENTS.map((a) => (
            <Chip
              key={a.flagCode}
              active={accent === a.flagCode}
              onClick={() => setAccent(a.flagCode)}
            >
              <ReactCountryFlag countryCode={a.flagCode} svg />
              {ACCENT_LABEL[a.flagCode]
                ? t(ACCENT_LABEL[a.flagCode])
                : a.displayName}
            </Chip>
          ))}
        </div>
      </div>
      <div className="tw-flex tw-flex-col tw-gap-2">
        <Label>{t("studio.tts.delivery")}</Label>
        <div className="tw-flex tw-gap-2 tw-overflow-x-auto tw-pb-1 md:tw-flex-wrap">
          {EXPRESSIONS.map((x) => (
            <Chip
              key={x.displayName}
              active={expression === x.displayName}
              onClick={() => setExpression(x.displayName)}
            >
              <span>{x.emoji}</span>
              {EXPRESSION_LABEL[x.displayName]
                ? t(EXPRESSION_LABEL[x.displayName])
                : x.displayName}
            </Chip>
          ))}
        </div>
      </div>
      <UseButton onClick={confirm} disabled={!text.trim()}>
        {t("studio.tts.use")}
      </UseButton>
    </div>
  );
};

export const AiSong = ({
  voiceModelName,
  onConfirm,
}: {
  voiceModelName: string;
  onConfirm: (g: {
    caption: string;
    lyrics: string;
    voice_model_name: string;
  }) => void;
}) => {
  const t = useT();
  const [caption, setCaption] = useState("");
  const [lyrics, setLyrics] = useState("");
  const [ownLyrics, setOwnLyrics] = useState(false);

  const confirm = () => {
    if (!caption.trim()) {
      toast.error(t("studio.ai.noStyle"));
      return;
    }
    if (caption.length > MAX_CAPTION) {
      toast.error(t("studio.ai.styleTooLong", { max: MAX_CAPTION }));
      return;
    }
    if (lyrics.length > MAX_LYRICS) {
      toast.error(t("studio.ai.lyricsTooLong", { max: MAX_LYRICS }));
      return;
    }
    onConfirm({
      caption: caption.trim(),
      lyrics: lyrics.trim(),
      voice_model_name: voiceModelName || "",
    });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3.5">
      <div className="tw-flex tw-flex-col tw-gap-2">
        <Label>{t("studio.ai.style")}</Label>
        <div className="tw-flex tw-gap-2 tw-overflow-x-auto tw-pb-1 md:tw-flex-wrap">
          {GENRE_PRESETS.map((g) => (
            <Chip
              key={g.label}
              active={caption === g.caption}
              onClick={() => setCaption(g.caption)}
            >
              {GENRE_LABEL[g.label] ? t(GENRE_LABEL[g.label]) : g.label}
            </Chip>
          ))}
        </div>
      </div>
      <TextBox
        aria-label={t("studio.ai.styleLabel")}
        placeholder={t("studio.ai.stylePlaceholder")}
        rows={2}
        value={caption}
        maxLength={MAX_CAPTION}
        onChange={(e) => setCaption(e.target.value)}
      />
      {ownLyrics ? (
        <TextBox
          aria-label={t("studio.ai.lyrics")}
          placeholder={t("studio.ai.lyricsPlaceholder")}
          rows={6}
          value={lyrics}
          maxLength={MAX_LYRICS}
          onChange={(e) => setLyrics(e.target.value)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setOwnLyrics(true)}
          className="tw-cursor-pointer tw-self-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-[13px] tw-font-extrabold tw-text-jam-purple"
        >
          {t("studio.ai.ownLyrics")}
        </button>
      )}
      <UseButton onClick={confirm} disabled={!caption.trim()}>
        {t("studio.ai.use")}
      </UseButton>
      {/* Required by Treblo's API terms: a prominent, legible link to
          treblo.com wherever generation is offered. Do not remove or shrink
          it without a waiver from api@treblo.com. */}
      <a
        href="https://treblo.com"
        target="_blank"
        rel="noopener noreferrer"
        className="tw-self-center tw-text-sm tw-font-bold tw-text-jam-muted tw-underline tw-underline-offset-2 hover:tw-text-jam-purple"
      >
        Powered by Treblo
      </a>
    </div>
  );
};
